import { Head, Link, usePage } from '@inertiajs/react'
import { ArrowLeft, Pencil, Plus } from 'lucide-react'
import type { ReactNode } from 'react'

import { PageHeader } from '@/components/PageHeader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { companiesPath, editCompanyPath, founderPath, newCompanyInvestmentPath } from '@/lib/routes'
import { getRememberedUrl } from '@/lib/rememberedUrl'
import type { SharedPageProps } from '@/types/inertia'

type Country = { code: string; name: string; flag: string }

type CompanyStatus = 'operating' | 'raising' | 'exited_ma' | 'exited_ipo' | 'dissolved'

type IndustryTag = { id: number; name: string; emoji: string | null }

type FounderAffiliation = {
  id: number
  founder: {
    id: number
    name: string
    slug: string
    avatar_url: string | null
  }
  role: string | null
  joined_at: string | null
  left_at: string | null
  active: boolean
}

type CompanyDetails = {
  id: number
  slug: string
  name: string
  legal_name: string | null
  status: CompanyStatus
  founded_year: number | null
  webpage: string | null
  linkedin: string | null
  female_founder: boolean
  oneliner_en: string | null
  oneliner_es: string | null
  description_en: string | null
  description_es: string | null
  logo_url: string | null
  country: Country | null
  batch: { id: number; code: string; label: string } | null
  industries: IndustryTag[]
  founders: FounderAffiliation[]
}

type CompaniesShowProps = { company: CompanyDetails }

const STATUS_LABEL: Record<CompanyStatus, string> = {
  operating: 'Operating',
  raising: 'Raising',
  exited_ma: 'Exited (M&A)',
  exited_ipo: 'Exited (IPO)',
  dissolved: 'Dissolved',
}

const STATUS_VARIANT: Record<CompanyStatus, 'default' | 'accent' | 'secondary' | 'destructive'> = {
  operating: 'default',
  raising: 'accent',
  exited_ma: 'secondary',
  exited_ipo: 'secondary',
  dissolved: 'destructive',
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-sm">{children}</div>
    </div>
  )
}

function CompaniesShow({ company }: CompaniesShowProps) {
  const backToIndex = getRememberedUrl('companies', companiesPath())
  const { auth } = usePage<SharedPageProps>().props
  const isPartner = auth.user?.role === 'partner'

  return (
    <>
      <Head title={`${company.name} — Companies`} />

      <div className="mx-auto max-w-4xl px-8 py-10">
        <Link
          href={backToIndex}
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to companies
        </Link>

        <div className="mb-6 flex items-start gap-4">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-muted">
            {company.logo_url ? (
              <img
                src={company.logo_url}
                alt=""
                className="h-full w-full object-contain"
              />
            ) : (
              <span className="text-lg text-muted-foreground">
                {company.name.slice(0, 1).toUpperCase()}
              </span>
            )}
          </div>
          <div className="flex-1">
            <PageHeader
              title={company.name}
              description={
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={STATUS_VARIANT[company.status]}>
                    {STATUS_LABEL[company.status]}
                  </Badge>
                  {company.country && (
                    <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                      <span>{company.country.flag}</span>
                      <span>{company.country.name}</span>
                    </span>
                  )}
                  {company.batch && (
                    <span className="text-sm text-muted-foreground">
                      · {company.batch.code}
                    </span>
                  )}
                </div>
              }
              actions={
                <div className="flex gap-2">
                  {isPartner && (
                    <Link href={newCompanyInvestmentPath(company.slug)}>
                      <Button>
                        <Plus />
                        Add investment
                      </Button>
                    </Link>
                  )}
                  <Link href={editCompanyPath(company.slug)}>
                    <Button variant="outline">
                      <Pencil />
                      Edit
                    </Button>
                  </Link>
                </div>
              }
            />
            {company.oneliner_en && (
              <p className="mt-2 text-sm text-muted-foreground">{company.oneliner_en}</p>
            )}
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Basic info</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <Field label="Legal name">{company.legal_name ?? '—'}</Field>
            <Field label="Founded">{company.founded_year ?? '—'}</Field>
            <Field label="Website">
              {company.webpage ? (
                <a
                  href={company.webpage}
                  target="_blank"
                  rel="noreferrer"
                  className="underline-offset-4 hover:underline"
                >
                  {company.webpage}
                </a>
              ) : (
                '—'
              )}
            </Field>
            <Field label="LinkedIn">
              {company.linkedin ? (
                <a
                  href={company.linkedin}
                  target="_blank"
                  rel="noreferrer"
                  className="underline-offset-4 hover:underline"
                >
                  {company.linkedin}
                </a>
              ) : (
                '—'
              )}
            </Field>
            <Field label="Industries">
              {company.industries.length === 0 ? (
                '—'
              ) : (
                <div className="flex flex-wrap gap-1">
                  {company.industries.map((i) => (
                    <span
                      key={i.id}
                      className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-xs"
                    >
                      {i.emoji && <span>{i.emoji}</span>}
                      <span>{i.name}</span>
                    </span>
                  ))}
                </div>
              )}
            </Field>
            <Field label="Female founder">{company.female_founder ? 'Yes' : 'No'}</Field>
          </CardContent>
        </Card>

        {company.description_en && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap text-sm">{company.description_en}</p>
            </CardContent>
          </Card>
        )}

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Founders</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {company.founders.length === 0 ? (
              <p className="px-6 py-8 text-sm text-muted-foreground">No founders recorded.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-xs text-muted-foreground">
                    <th className="px-6 py-2 text-left font-medium">Name</th>
                    <th className="px-6 py-2 text-left font-medium">Role</th>
                    <th className="px-6 py-2 text-left font-medium">From</th>
                    <th className="px-6 py-2 text-left font-medium">To</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {company.founders.map((a) => (
                    <tr key={a.id}>
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-3">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-muted">
                            {a.founder.avatar_url ? (
                              <img
                                src={a.founder.avatar_url}
                                alt=""
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <span className="text-[10px] text-muted-foreground">
                                {a.founder.name.slice(0, 1).toUpperCase()}
                              </span>
                            )}
                          </div>
                          <Link
                            href={founderPath(a.founder.slug)}
                            className="underline-offset-4 hover:underline"
                          >
                            {a.founder.name}
                          </Link>
                        </div>
                      </td>
                      <td className="px-6 py-3 text-muted-foreground">{a.role ?? '—'}</td>
                      <td className="px-6 py-3 text-muted-foreground">{a.joined_at ?? '—'}</td>
                      <td className="px-6 py-3 text-muted-foreground">
                        {a.active ? (
                          <span className="text-emerald-600 dark:text-emerald-400">active</span>
                        ) : (
                          (a.left_at ?? '—')
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  )
}

export default Object.assign(CompaniesShow, {
  layout: (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>,
})
